import React, { useState, useEffect } from "react"
import { useTheme, useMediaQuery, Grid, Hidden, IconButton } from "@material-ui/core"
import moment from "moment-timezone"
import { useHistory, useParams } from "react-router-dom"
import { FilterList } from "@material-ui/icons"
import Config from "react-global-configuration"
import {
  AreaHeader,
  DateNavigatorBar,
  JobViewer,
  JobsFilters,
  JobsSearch,
  DesktopCreateJobButton,
  MobileCreateJobButton,
  FormatDateFormal,
  ModuleHeading,
  Content,
  JobCreator,
  ScrollToTop,
  IconMessage,
  DeviceGroupsDisplay,
  BackToTop,
  JobList,
} from "../../components"
import { jobFiltersStateVar, useLazyQueryJobs, useQuerySettings } from "../../data"
import { toId, useDateUtils, useJobUtils, useMountAwareReactiveVar, useMountEffect } from "../../utils"
import { useAuth } from "../../services"

const Jobs = () => {
  const { jobsMs } = Config.get("polling")
  const theme = useTheme()
  const history = useHistory()
  const { day, type, id, location: switchLocation } = useParams()
  const mdDown = useMediaQuery(theme.breakpoints.down("md"))
  const { getNavigateDateLink, isAfterToday } = useJobUtils()
  const { momentToScheduleDate } = useDateUtils()
  const jobsFiltersStore = useMountAwareReactiveVar(jobFiltersStateVar)
  const {
    location,
    setLocation,
    settings: { deviceGroups },
  } = useAuth()
  const { lang } = useQuerySettings()

  const [editJob, setEditJob] = useState(null)
  const [viewJob, setViewJob] = useState(null)
  const [date, setDate] = useState(null)
  const [unsubscribes, setUnsubscribes] = useState(null)
  const [loadJobs, jobsResult] = useLazyQueryJobs()
  const { called, data, refetch, subscribe, startPolling, stopPolling } = jobsResult
  const [locationId, setlocationId] = useState(null)
  const [openFilter, setOpenFilter] = useState(false)
  const [backToTopOffset, setBackToTopOffset] = useState(null)

  useMountEffect(() => {
    setBackToTopOffset(document.querySelector("#date-navigator-bar").clientHeight + 20)
  })

  useEffect(() => {
    if (
      location &&
      location.id &&
      (locationId !== location.id || (moment(day).isValid() && !moment.tz(day, location.timeZone).isSame(date)))
    ) {
      const newLocationId = location.id
      const newDate = moment.tz(day, location.timeZone).startOf("day")
      setlocationId(newLocationId)
      setDate(newDate)
      jobFiltersStateVar({
        ...jobsFiltersStore,
        isFuture: isAfterToday(newDate),
      })
      if (stopPolling) stopPolling()
      if (called) refetch({ location: newLocationId, date: momentToScheduleDate(newDate), groups: deviceGroups })
      else {
        loadJobs({ variables: { location: newLocationId, date: momentToScheduleDate(newDate), groups: deviceGroups } })
      }
    }
  }, [
    called,
    date,
    day,
    deviceGroups,
    isAfterToday,
    jobsFiltersStore,
    loadJobs,
    location,
    locationId,
    momentToScheduleDate,
    refetch,
    stopPolling,
  ])

  useEffect(() => {
    if (data && startPolling && jobsMs) startPolling(jobsMs)
  }, [data, jobsMs, startPolling])

  useEffect(() => {
    if (switchLocation && switchLocation !== toId(location)) setLocation(switchLocation)
  }, [location, setLocation, switchLocation])

  useEffect(() => {
    if (id && id !== "new" && data) {
      const job = data.jobs.items.find((j) => j.id === id)
      if (job) setViewJob(job)
    }
  }, [id, data, type])

  useEffect(() => {
    if (called && !unsubscribes) {
      setUnsubscribes(subscribe())
      return () => {
        if (unsubscribes) {
          unsubscribes.map((unsub) => unsub())
        }
      }
    }
  }, [called, subscribe, unsubscribes, setUnsubscribes])

  const handleEditJob = (post) => {
    setEditJob(post)
  }

  const handleCreate = (createType) => {
    history.push(`${getNavigateDateLink(date, location.timeZone)}/${createType}/new`)
  }
  const handleCreateClose = () => {
    history.push(getNavigateDateLink(date, location.timeZone))
  }

  const handleViewJobClose = () => {
    setViewJob(null)
    history.push(getNavigateDateLink(date, location.timeZone))
  }

  const handleViewJobDelete = () => {
    setViewJob(null)
    history.push(getNavigateDateLink(date, location.timeZone))
    refetch({ location: locationId, date: momentToScheduleDate(date) })
  }

  const handleDateNavigate = (move) => {
    let newDate = moment.tz(day, location.timeZone)
    if (move === -1) newDate.subtract(1, "days")
    else if (move === 1) newDate.add(1, "days")
    else newDate = move

    history.push(getNavigateDateLink(newDate, location.timeZone))
  }

  const handleFilterTextChange = (filterText) => {
    jobFiltersStateVar({
      ...jobsFiltersStore,
      filterText,
    })
  }

  const handleSortChange = (sort) => {
    jobFiltersStateVar({
      ...jobsFiltersStore,
      sort,
    })
  }

  const handleToggleFilter = () => {
    setOpenFilter(!openFilter)
  }

  const createJob = id === "new" && type

  const scrollToTop = mdDown && (viewJob || createJob)

  return (
    <>
      {scrollToTop && <ScrollToTop />}

      <JobCreator open={createJob} onClose={handleCreateClose} edit={editJob} />

      <JobViewer job={viewJob} onClose={handleViewJobClose} onDelete={handleViewJobDelete} />

      {(!mdDown || (mdDown && !id)) && (
        <>
          <AreaHeader
            title={
              mdDown ? (
                <>
                  Jobs for <FormatDateFormal value={date} />
                </>
              ) : (
                lang.area.jobs
              )
            }
            subTitle={<DeviceGroupsDisplay />}
            titleIcon="jobs-light"
            desktopButtons={<DesktopCreateJobButton onCreateClick={handleCreate} />}
            mobileButtons={<MobileCreateJobButton onCreateClick={handleCreate} />}
          />

          <DateNavigatorBar date={day} onDateNavigate={handleDateNavigate} />

          <Content full>
            <Grid container direction="row">
              <Hidden mdDown>
                <Grid item lg="auto" md={12}>
                  <JobsFilters open={openFilter} onClose={handleToggleFilter} fullWidth={mdDown} />
                  {Boolean(backToTopOffset) && <BackToTop from={300} offset={backToTopOffset} />}
                </Grid>
              </Hidden>
              <Hidden lgUp>
                <JobsFilters open={openFilter} onClose={handleToggleFilter} fullWidth={mdDown} />
              </Hidden>
              <Grid item xs={12} lg>
                <Hidden mdDown>
                  <ModuleHeading>
                    Jobs for <FormatDateFormal value={date} />
                  </ModuleHeading>
                </Hidden>
                <JobsSearch
                  onTextChange={handleFilterTextChange}
                  onSortChange={handleSortChange}
                  textValue={jobsFiltersStore.filterText}
                  sortValue={jobsFiltersStore.sort}
                  textRight={
                    mdDown && (
                      <IconButton title="Filters" onClick={handleToggleFilter}>
                        <FilterList />
                      </IconButton>
                    )
                  }
                />
                <JobList
                  {...jobsResult}
                  onEdit={handleEditJob}
                  filters={jobsFiltersStore}
                  date={date}
                  empty={
                    <IconMessage name="empty-activejobs" text="There's no active jobs currently listed for this day." />
                  }
                />
              </Grid>
            </Grid>
          </Content>
        </>
      )}
    </>
  )
}

export default Jobs
